///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext';
import { currentSubMonth, daysUntilExpiry, daysUntilLinesReset, isStillActive, subDisplayData,
  subscriptionExpireDate, subscriptionNames, subscriptionUsage } from '../../services/SubscriptionsService';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faX, faSpinner, faCopy, faCreditCard, faBuildingColumns } from '@fortawesome/free-solid-svg-icons'
import { formatNr } from '../../services/FormatService';
import { userSideId } from '../../services/IdService';
import ReactTooltip from 'react-tooltip';
import Popup from '../Popup';
import CardPaymentForm from './CardPaymentForm';
import './Subscriptions.css';

const Subscriptions = ({ goToPayments }) => {
  const {
    userData,
    currentUser,
    newSubscription,
    loadingAction,
    cardPaymentsAvailable,
    layouts,
    companies
  } = useAuth()

  const [openPopup, setOpenPopup]   = useState(false)
  const [popupData, setPopupData]   = useState({})
  const [subDisplay, setSubDisplay] = useState([])
  const [isOpen, setIsOpen]         = useState(false)
  const [chartData, setChartData]   = useState([])
  const [isLoading, setIsLoading]   = useState(false)
  const [copiedId, setCopiedId]     = useState(null)
  //? set once the order exists and the customer is on the card step of the same popup
  const [cardInvoice, setCardInvoice] = useState(null)
  const [payingWith, setPayingWith] = useState('')

  const handleCopyId = (fullId) => {
    navigator.clipboard.writeText(fullId)
    setCopiedId(fullId)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const chartColors = ['--chart-good', '--chart-warn', '--chart-bad'];

  const getPerc = (min, max) => {
    if (min <= 0) return 0
    if (min >= max) return 100
    return Math.round((min * 100)/max)
  }

  const setChartDisplayData = () => {
    if (userData.subscriptions.length === 0) return
    const uSub = userData.subscriptions[0]
    if (Object.keys(uSub).length === 0) return
    if (!uSub.valid || uSub.manualDisable) return 
    const safeMonthIdx = Math.min(currentSubMonth(uSub.date), parseInt(uSub.duration) - 1)
    const currLines = uSub.lines[safeMonthIdx] !== undefined ? parseInt(uSub.lines[safeMonthIdx]) : 0
    const currLinesPerc = getPerc(currLines, parseInt(uSub.linesStart))
    const currMPerc = getPerc((uSub.duration - currentSubMonth(uSub.date)), parseInt(uSub.duration))
    const toAdd = [
        {
            maxVal: uSub.linesStart,
            currVal: currLines,
            currPerc: currLinesPerc,
            title: 'Linii rămase din lună',
            color: currLinesPerc > 30 ? chartColors[0] : currLinesPerc > 15 ? chartColors[1] : chartColors[2]
        },
        {
            maxVal: uSub.duration,
            currVal: uSub.duration - currentSubMonth(uSub.date),
            currPerc: currMPerc,
            title: 'Luni rămase',
            color: currMPerc > 30 ? chartColors[0] : currMPerc > 15 ? chartColors[1] : chartColors[2]
        }
    ]
    setChartData(toAdd)
  }

  useEffect(() => {
    setSubDisplay(subDisplayData())
    setChartDisplayData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Usage statistics for the subscription in use, following the same rule as the gauges
  //? above: an unpaid or disabled subscription has nothing to report on.
  //-----------------------------------------------------------------------------------------
  const statsSub = (() => {
    const sub = userData.subscriptions[0]
    if (!sub || Object.keys(sub).length === 0) return null
    if (!sub.valid || sub.manualDisable) return null
    return sub
  })()

  const usage = statsSub ? subscriptionUsage(statsSub) : null
  const linesResetIn = statsSub ? daysUntilLinesReset(statsSub) : null
  //? null means there is no next month to refill - what is left is the expiry itself
  const expiresIn = statsSub && linesResetIn === null
    ? daysUntilExpiry(statsSub.date, statsSub.duration)
    : null

  const dayCount = (days) => days <= 0 ? 'Azi' : days === 1 ? 'Mâine' : `${days} zile`

//   const monthCount = (nr) => nr === 1 ? '1 lună' : `${nr} luni`

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Reads the consumption back to the user as plain advice. It has to be able to say "your
  //? package fits" as readily as "you need a bigger one", otherwise it is just an advert.
  //-----------------------------------------------------------------------------------------
//   const planHint = (() => {
//     if (!usage || usage.averageUsed === null) return null
//     const perc = Math.round((usage.averageUsed / usage.allowance) * 100)
//     if (usage.exhaustedMonths > 0) return {
//         tone: 'warn',
//         upgrade: true,
//         text: `Ați rămas fără linii în ${monthCount(usage.exhaustedMonths)} din abonamentul curent. `
//             + 'Un pachet mai mare v-ar oferi mai mult spațiu de lucru.'
//     }
//     //? one month is not a trend - never push an upgrade off a single data point
//     if (usage.completedMonths >= 2 && perc >= 80) return {
//         tone: 'warn',
//         upgrade: true,
//         text: `Folosiți în medie ${perc}% din liniile lunare, deci sunteți aproape de limita pachetului.`
//     }
//     if (perc <= 25) return {
//         tone: 'good',
//         text: `Folosiți în medie ${perc}% din liniile lunare. Pachetul acoperă confortabil consumul dvs.`
//     }
//     return {
//         tone: 'good',
//         text: `Folosiți în medie ${perc}% din liniile lunare. Pachetul actual se potrivește consumului dvs.`
//     }
//   })()

//   const openPlans = () => {
//     setIsOpen(true)
//     window.scrollTo({ top: 0, behavior: 'smooth' })
//   }

  const handleOpenPopup = (data) => {
    const dataToAdd = {
        type: 3,
        data,
        title: 'Confirmați achziția'
    }
    setCardInvoice(null)
    setPayingWith('')
    setPopupData(dataToAdd)
    setOpenPopup(true)
  }

  const closePopup = () => {
    setOpenPopup(false)
    setCardInvoice(null)
    setPayingWith('')
    //? The order exists from the moment the card option is chosen, and the rest of the app
    //? still shows the state from before it. Reloading keeps "Plăți" and the subscription
    //? table honest about what was just created.
    if (cardInvoice) window.location.reload()
  }

  const getTotalPrice = (price, months = 12) => {
    return (parseFloat(price) * months).toFixed(2)
  }

  //-----------------------------------------------------------------------------------------
  //* \brief
  //? Creates the subscription and its proforma.
  //?
  //? Both payment methods take this same path - the order is always created first. With
  //? "card" the popup then moves on to the billing step instead of closing, and nothing is
  //? activated until NETOPIA confirms the payment.
  //-----------------------------------------------------------------------------------------
  const purchaseSubscription = async (data, payByCard = false) => {
    if (loadingAction || isLoading) return
    setPayingWith(payByCard ? 'card' : 'transfer')
    const today = new Date();
    // Example object data
    // {
    //     "id": 2, // = type
    //     "name": "Plus",
    //     "lines": 1300,
    //     "time": 12,
    //     "price": 19.99,
    //     "options": {
    //         "exchange": true,
    //         "valStat": true,
    //         "vat": true,
    //         "layouts": 3,
    //         "companies": 1
    //     }
    // }
    setIsLoading(true)
    const subData = {
        uid: currentUser.uid,
        name: data.id,
        date: today.toString(),
        timeStamp: today,
        duration: data.time,
        lines: new Array(parseInt(data.time)).fill(data.lines),
        linesStart: data.lines,
        cifCheck: data.options.vat,
        compAvailable: data.options.companies,
        valStatCalc: data.options.valStat,
        layoutsAvailable: data.options.layouts,
        manualDisable: false,
        valid: false,
        price: data.price
    }
    const invoiceData = {
        // id,
        uid: currentUser.uid,
        type: '0',
        // series,
        // number,
        // seriesNumber,
        status: '0',
        timestamp: new Date(),
        created: new Date(),
        paidDate: '',
        dayMonthYear: `${new Date().getDate()}-${new Date().getMonth()+1}-${new Date().getFullYear()}`,
        // price ,
        // currencyRate,
        subscription: {
            // id,
            name: data.name,
            period: data.time,
            quantity: 1,
            priceEur: +data.price * +data.time
        },
        client: {
            name: userData.data.company,
            cui: userData.data.cui,
            regCom: userData.data.regCom,
            address: userData.data.address,
            county: userData.data.county,
            email: currentUser.email
        }
    }
    const result = await newSubscription(subData, invoiceData, { payByCard })
    setIsLoading(false)

    if (!result || !result.ok) {
        setPayingWith('')
        return
    }

    //? Bank transfer keeps the original behaviour: the popup closes and newSubscription
    //? reloads the page by itself.
    if (!payByCard) {
        setOpenPopup(false)
        return
    }

    setCardInvoice(result.invoice)
  }

  const getTax = (val) => {
    return ((21/100)* +val).toFixed(2)
  }

  const getWithTax = (val) => {
    const tax = 21/100
    return (tax * (+val) + (+val))
  }

  const popupContent = (data) => (
    <div className='md-form'>
        <div className='md-summary'>
            <div className='md-summary__item'>
                <span>Denumire</span>
                <span>{data.name}</span>
            </div>
            <div className='md-summary__item'>
                <span>Durată</span>
                <span>{data.time} luni</span>
            </div>
            <div className='md-summary__item'>
                <span>Linii / lună</span>
                <span>{formatNr(data.lines)}</span>
            </div>
            <div className='md-summary__item'>
                <span>Machete</span>
                <span>{data.options.layouts}</span>
            </div>
            <div className='md-summary__item'>
                <span>Firme</span>
                <span>{data.options.companies}</span>
            </div>
            <div className='md-summary__item'>
                <span>Expiră la</span>
                <span>{subscriptionExpireDate(new Date(), data.time)}</span>
            </div>
        </div>

        <div className='md-form__divider'></div>

        <div className='md-summary'>
            <div className='md-summary__item'>
                <span>Preț fără TVA</span>
                <span>{formatNr(getTotalPrice(data.price, 12))}€</span>
            </div>
            <div className='md-summary__item'>
                <span>TVA</span>
                <span>{formatNr(getTax(getTotalPrice(data.price, 12)))}€</span>
            </div>
            <div className='md-summary__item'>
                <span>Preț TOTAL</span>
                <span style={{color: 'var(--contrast-font-color)'}}>{formatNr(getWithTax(getTotalPrice(data.price, 12)))}€</span>
            </div>
        </div>

        <div className='md-form__divider'></div>

        <div className='md-form__warning'>! Atenție !</div>
        <div className='md-form__warning'>Abonamentul anterior va fi dezactivat odată cu achiziția.</div>

        <div className='md-form__divider'></div>

        {cardPaymentsAvailable ? (
        <>
            <div style={{textAlign:'center', fontWeight:'bold', paddingBottom:'0.25em'}}>
                Alegeți modalitatea de plată
            </div>

            <div className='ap-popup-info' style={{textAlign:'left'}}>
                <div>
                    <b><FontAwesomeIcon icon={faCreditCard}/> Plată cu cardul</b> - abonamentul se
                    activează <b>automat, imediat</b> după confirmarea plății. Plata se face în Lei,
                    pe pagina securizată NETOPIA Payments.
                </div>
                <div style={{marginTop:'0.5em'}}>
                    <b><FontAwesomeIcon icon={faBuildingColumns}/> Transfer bancar</b> - primiți o
                    factură proformă cu datele de plată. Abonamentul se activează <b>după ce
                    confirmăm încasarea</b> (de regulă 1-2 zile lucrătoare).
                </div>
            </div>

            <div style={{display:'flex', gap:'0.75em', justifyContent:'center', flexWrap:'wrap', paddingTop:'0.75em'}}>
                <button onClick={() => purchaseSubscription(data, true)} className='md-btn md-btn--primary'>
                    {isLoading && payingWith === 'card'
                        ? <FontAwesomeIcon className='spinner' icon={faSpinner}/>
                        : <><FontAwesomeIcon icon={faCreditCard}/> Plătește cu cardul</>
                    }
                </button>
                <button onClick={() => purchaseSubscription(data, false)} className='md-btn md-btn--ghost'>
                    {isLoading && payingWith === 'transfer'
                        ? <FontAwesomeIcon className='spinner' icon={faSpinner}/>
                        : <><FontAwesomeIcon icon={faBuildingColumns}/> Plată prin transfer bancar</>
                    }
                </button>
            </div>

            <div className='ap-popup-hint' style={{textAlign:'center', paddingTop:'0.75em'}}>
                În ambele cazuri comanda se înregistrează acum; puteți schimba metoda de plată
                oricând din secțiunea "Plăți".
            </div>
        </>
        ) : (
        /* Card payments switched off - the flow is exactly what it was before they existed */
        <>
            <div className='ap-popup-info' style={{textAlign:'left'}}>
                <div>
                    <b><FontAwesomeIcon icon={faBuildingColumns}/> Plată prin transfer bancar</b> -
                    după confirmarea comenzii primiți o factură proformă cu datele de plată, în
                    secțiunea "Plăți". Abonamentul se activează <b>după confirmarea încasării</b>
                    (de regulă 1-2 zile lucrătoare).
                </div>
            </div>

            <div style={{textAlign:'center', paddingTop:'0.75em'}}>
                <button onClick={() => purchaseSubscription(data, false)} className='md-btn md-btn--primary'>
                    {isLoading
                        ? <FontAwesomeIcon className='spinner' icon={faSpinner}/>
                        : 'Achiziționează'
                    }
                </button>
            </div>
        </>
        )}
    </div>
  )

  const renderFeatureCheck = (has) => (
    <span className={`sb-check ${has ? 'sb-check--yes' : 'sb-check--no'}`}>
        <FontAwesomeIcon icon={has ? faCircleCheck : faX} />
    </span>
  )

  const POPULAR_INDEX = 2 // Plus plan

  return (
    <div className='app-default-wrapper'>
        <div className='app-default-section'>
            <div className='app-default-header'>
                <button
                    className={`sb-toggle ${isOpen ? 'sb-toggle--close' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? 'Închide' : 'Abonament nou'}
                </button>
            </div>
            {isOpen &&
            <>
            <div className='sb-grid'>
                {subDisplay.map((data) => {
                    const isPopular = data.id === POPULAR_INDEX
                    return(
                    <div key={`${data.id} - ${data.name}`}
                        onClick={() => handleOpenPopup(data)}
                        className={`sb-card ${isPopular ? 'sb-card--popular' : ''}`}
                    >
                        {isPopular && <div className='sb-badge'>Popular</div>}
                        <div className='sb-card__name'>{data.name}</div>
                        <div className='sb-card__price'>
                            <span className='sb-card__currency'>€</span>
                            <span className='sb-card__amount'>{data.price}</span>
                            <span className='sb-card__period'>/lună</span>
                        </div>
                        <ul className='sb-card__features'>
                            <li>
                                <span>Linii / lună</span>
                                <strong>{formatNr(data.lines)}</strong>
                            </li>
                            <li>
                                <span>Machete</span>
                                <strong>{data.options.layouts}</strong>
                            </li>
                            <li>
                                <span>Firme</span>
                                <strong>{data.options.companies}</strong>
                            </li>
                            <li>
                                <span>Verificare CIF</span>
                                {renderFeatureCheck(data.options.vat)}
                            </li>
                            <li>
                                <span>Valoare statistică</span>
                                {renderFeatureCheck(data.options.valStat)}
                            </li>
                            <li>
                                <span>Conversie valutară BNR</span>
                                {renderFeatureCheck(data.options.exchange)}
                            </li>
                        </ul>
                        <div className='sb-card__cta'>Alege planul</div>
                    </div>
                    )
                })
                }
            </div>
            <div className='sb-notes'>
                <small>Toate abonamentele sunt facturate pe perioada duratei acestora.</small>
                <small>Pentru o ofertă personalizată vă rugăm să ne contactați.</small>
                <small>Prețurile afișate NU includ TVA.</small>
            </div>
            </>
            }
            <div className="table-fixed-header">
                <table className='simple-table'>
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Abonament</th>
                        <th>Perioadă</th>
                        <th>Expirare</th>
                        <th>Linii rămase din lună</th>
                        <th>Stare</th>
                    </tr>
                    </thead>
                    {userData.subscriptions.map(sub => {
                        return(
                        <tbody key={sub.subId}>
                        <tr>
                            <td>
                                <span style={{display:'inline-flex', alignItems:'center', gap:'0.5em'}}>
                                    <span title={userSideId(sub.subId)}>{userSideId(sub.subId).slice(0, 3)}…</span>
                                    <FontAwesomeIcon
                                        icon={copiedId === userSideId(sub.subId) ? faCircleCheck : faCopy}
                                        title={copiedId === userSideId(sub.subId) ? 'Copiat!' : 'Copiază ID'}
                                        onClick={() => handleCopyId(userSideId(sub.subId || ""))}
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '0.8em',
                                            color: copiedId === sub.subId ? 'var(--button-color-main)' : 'var(--faded-font-color)'
                                        }}
                                    />
                                </span>
                            </td>
                            <td>{subscriptionNames(sub.name)}</td>
                            <td>{`${sub.duration} luni`}</td>
                            <td>{subscriptionExpireDate(sub.date, sub.duration)}</td>
                            <td>{!sub.manualDisable ? formatNr(sub.lines[Math.min(currentSubMonth(sub.date), parseInt(sub.duration) - 1)] ?? 0) : 0}</td>
                            <td data-tip data-for={
                                (!sub.valid && !sub.manualDisable ) ? 'sub-invalid' :
                                isStillActive(sub.date, sub.duration, sub.manualDisable) ? 'sub-active' : 'sub-expired'
                                }>
                                {/* Pending: an orange X alone says something is wrong but not
                                    what to do about it, so it carries the way out with it */}
                                {(!sub.valid && !sub.manualDisable ) ?
                                <span className='sub-pending'>
                                    <FontAwesomeIcon icon={faX} />
                                    {goToPayments &&
                                        <button className='sub-pending__cta' onClick={goToPayments}
                                            title='Mergi la secțiunea Plăți pentru a activa abonamentul'>
                                            <FontAwesomeIcon icon={faCreditCard} />
                                            <span>Achită</span>
                                        </button>
                                    }
                                </span> :
                                isStillActive(sub.date, sub.duration, sub.manualDisable) ?
                                <span style={{color:"green"}}><FontAwesomeIcon icon={faCircleCheck} /></span> :
                                <span style={{color:"red"}}><FontAwesomeIcon icon={faX} /></span>
                                }
                            </td>
                        </tr>
                        </tbody>
                        )
                    })}
                </table>
            </div>
            <div className='gauges-row'>
                {
                    chartData.length > 0 &&
                    chartData.map((val, i) => {
                        return(
                            <div className='gauge-default' key={`${val.title} - ${i} - ${val.currPerc}`}>
                                <h3>{val.title}</h3>
                                <div className="semi-donut" style={{"--percentage-semi-donut": `${val.currPerc}`, 
                                    "--fill": `var(${val.color})`}}>
                                    <div>{formatNr(val.currVal)}</div>
                                </div>
                            </div>
                        )
                    })
                }
            </div>

            {/* ── Usage statistics ─────────────────────────────────────── */}
            {usage &&
                <div className='su-usage'>
                    <span className='su-usage__title'>Consumul abonamentului</span>

                    <div className='su-stats-grid'>
                        <div className='su-stat'>
                            <span className='su-stat__value'>
                                {linesResetIn !== null ? dayCount(linesResetIn) : dayCount(expiresIn)}
                            </span>
                            <span className='su-stat__label'>
                                {linesResetIn !== null ? 'Până la reîncărcare' : 'Până la expirare'}
                            </span>
                        </div>
                        {/* <div className='su-stat'>
                            <span className='su-stat__value'>
                                {usage.averageUsed !== null ? formatNr(usage.averageUsed) : '—'}
                            </span>
                            <span className='su-stat__label'>Medie linii / lună</span>
                        </div> */}
                        <div className='su-stat'>
                            <span className='su-stat__value'>{formatNr(usage.peakUsed)}</span>
                            <span className='su-stat__label'>Vârf lunar</span>
                        </div>
                        <div className='su-stat'>
                            <span className='su-stat__value'>{formatNr(usage.totalUsed)}</span>
                            <span className='su-stat__label'>Total linii folosite</span>
                        </div>
                        <div className='su-stat'>
                            <span className='su-stat__value'>
                                {layouts.length}<span className='su-stat__of'>/{statsSub.layoutsAvailable}</span>
                            </span>
                            <span className='su-stat__label'>Machete folosite</span>
                        </div>
                        <div className='su-stat'>
                            <span className='su-stat__value'>
                                {companies.length}<span className='su-stat__of'>/{statsSub.compAvailable}</span>
                            </span>
                            <span className='su-stat__label'>Firme adăugate</span>
                        </div>
                    </div>

                    <div className='su-chart'>
                        <div className='su-chart__bars'>
                            {usage.months.map(month => {
                                const perc = Math.round((month.used / usage.allowance) * 100)
                                const tone = perc >= 90 ? 'bad' : perc >= 70 ? 'warn' : 'good'
                                return (
                                    <div key={month.month}
                                        className={`su-bar ${month.isCurrent ? 'su-bar--current' : ''}`}
                                        title={`Luna ${month.month} de abonament`
                                            + (month.range ? ` (${month.range.rangeLabel})` : '')
                                            + `: ${formatNr(month.used)} din ${formatNr(usage.allowance)} linii folosite`}>
                                        <span className='su-bar__value'>{formatNr(month.used)}</span>
                                        <div className='su-bar__track'>
                                            <div className={`su-bar__fill su-bar__fill--${tone}`}
                                                style={{ height: `${perc}%` }}></div>
                                        </div>
                                        <span className='su-bar__label'>L{month.month}</span>
                                    </div>
                                )
                            })}
                        </div>
                        <div className='su-chart__legend'>
                            Consum raportat la {formatNr(usage.allowance)} linii / lună.
                            {' '}L1, L2... sunt lunile abonamentului, calculate din ziua achiziției
                            {' '}și nu de la începutul lunii calendaristice.
                            {usage.current.range && ` Perioada în curs: ${usage.current.range.rangeLabel}.`}
                        </div>
                    </div>

                    {/* {planHint ?
                        <div className={`su-hint su-hint--${planHint.tone}`}>
                            <span>{planHint.text}</span>
                            {planHint.upgrade &&
                                <button className='su-hint__cta' onClick={openPlans}>
                                    <FontAwesomeIcon icon={faArrowUp}/>
                                    <span>Vezi pachetele</span>
                                </button>
                            }
                        </div> :
                        <div className='su-hint su-hint--neutral'>
                            <span>Comparația cu lunile anterioare devine disponibilă după prima lună completă de abonament.</span>
                        </div>
                    } */}
                </div>
            }
        </div>

        {
            openPopup &&
            <Popup
                data = {{ ...popupData, title: cardInvoice ? 'Plata cu cardul' : popupData.title }}
                close = { closePopup }
            >
                {
                    cardInvoice
                    ? <CardPaymentForm invoice={cardInvoice} onClose={closePopup} justCreated={true} />
                    : popupContent(popupData.data)
                }
            </Popup>
        }

        <ReactTooltip effect="solid" id='sub-active' type='success'>Activ</ReactTooltip>
        <ReactTooltip effect="solid" id='sub-expired' type='error'>Inactiv</ReactTooltip>
        <ReactTooltip effect="solid" id='sub-invalid' type='warning'>
            <p><b>Invalid</b></p>
            <p>Efectuați plata în secțiunea "Plăți" pentru validare.</p>
        </ReactTooltip>
    </div>
  )
}

export default Subscriptions